import { ApolloLink, type StoreObject, type TypePolicies } from "@apollo/client";

export const createRootTrackingKeyLink = (trackingKey?: string) =>
  new ApolloLink((operation, forward) => {
    if (!trackingKey) {
      return forward(operation);
    }
    const context = operation.getContext();

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    context.headers = {
      ...context?.headers,
      "indeed-root-tracking-key": trackingKey,
    };
    operation.setContext(context);
    return forward(operation);
  });

type HttpHeader = {
  name: string;
  value: string;
};

export const createHttpHeaderLink = (headers: HttpHeader[]) =>
  new ApolloLink((operation, forward) => {
    if (headers.length === 0) {
      return forward(operation);
    }
    const context = operation.getContext();

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    context.headers = {
      ...context?.headers,
      ...headers.reduce(
        (acc, header) => {
          acc[header.name] = header.value;
          return acc;
        },
        {} as Record<string, string>,
      ),
    };
    operation.setContext(context);
    return forward(operation);
  });

export const typePolicies: TypePolicies = {
  Query: {
    fields: {
      getJsInterviewPrepOccupations: {
        merge(existing, incoming) {
          // Merge supportedOccupations and recommendedOccupations separately
          // since they come from different queries but same GraphQL field

          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return {
            ...existing,
            ...incoming,
          };
        },
      },
      findCoWorkerConversations: {
        keyArgs: ["orderBy"],
        merge(existing, incoming, { readField }) {
          // The logic here cannot be written in type safe way; we cannot avoid "any" (e.g. readField's return value is `any`). So be careful when modifying the logic

          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          const existingCoWorkerConversations = existing?.coWorkerConversations ?? {};

          // We basically follow the same pattern as https://www.apollographql.com/docs/react/pagination/cursor-based#using-a-map-to-store-unique-items

          const incomingCoWorkerConversations: Record<string, unknown> = {};

          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          incoming.coWorkerConversations.forEach((conversation: unknown) => {
            const id = readField("id", conversation as StoreObject);
            if (typeof id === "string") {
              incomingCoWorkerConversations[id] = conversation;
            }
          });

          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return {
            ...existing,
            ...incoming,

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            coWorkerConversations: {
              // The order here matters. incomingCoWorkerConversations should come first since we use pagination via "before" argument
              ...incomingCoWorkerConversations,
              ...existingCoWorkerConversations,
            },
          };
        },

        read(existing) {
          if (existing) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return {
              ...existing,

              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
              coWorkerConversations: Object.values(existing.coWorkerConversations),
            };
          }
        },
      },
    },
  },
  Job: {
    keyFields: ["key"],
  },
  JobSeekerProfile: {
    keyFields: ["accountKey"],
  },
};
